import { Button, Card, Table, Typography } from 'antd';
import React, { FC, useMemo, useState } from 'react';
import { localize } from '../helpers';
import ButtonBack from './../../../icons/arrow-left-icon.svg';
import './api-template.scss';
import { templates } from './template';
import ContactsModal from '/react4xp/parts/footer/components/contacts-modal/contacts-modal';
import { EmailSource } from '/react4xp/parts/main/actions.types';
import { checkAuthorization } from '../check-authorization/check-authorization';

const { Title, Text } = Typography;

const data = (translations: Record<string, string>) => [
    { key: '1', col1: '200', col2: localize('api-request-success', translations) },
    { key: '2', col1: '400', col2: localize('api-syntax-error', translations) },
    { key: '3', col1: '403', col2: localize('api-unauthorized-user', translations) },
    { key: '4', col1: '404', col2: localize('api-service-access-denied', translations) },
    { key: '5', col1: '500', col2: localize('api-server-error', translations) },
];

const columns = (translations: Record<string, string>) => [
    { title: localize('api-error-code', translations), dataIndex: 'col1', key: 'col1' },
    { title: localize('api-description', translations), dataIndex: 'col2', key: 'col2' },
];

const ApiTemplate: FC<any> = (props: any) => {
    const {
        nameServices,
        link,
        pdf,
        method,
        params,
        titleBody,
        requestBody,
        resultExample,
        setIsActive,
        translations,
        services,
    } = props;
    const [isOpenContactsModal, setIsOpenContactsModal] = useState(false);
    const { token, language, commonTranslations } = props;

    const handleBack = () => {
        setIsActive(false);
    };

    const api_download_needed = localize('api-download-program-set', translations).split(
        '__PROGRAM__'
    );

    const openContactsModal = () => {
        if (!token) {
            return checkAuthorization(
                { response: { status: 401 } },
                language,
                'contact-modal',
                commonTranslations
            );
        } else {
            setIsOpenContactsModal(true);
        }
    };

    const closeContactsModal = () => {
        setIsOpenContactsModal(false);
    };

    const handleOkContactsModal = () => {
        setIsOpenContactsModal(false);
    };

    const renderService = (service: any) => (
        <Card
            className="text__color"
            style={{ marginBottom: 20 }}
        >
            <Title
                level={5}
                className="text__color"
            >
                {service.nameServices}
            </Title>
            <Text>{service.link}</Text>
            <Title
                level={5}
                className="text__color"
            >
                {localize('api-request-type', translations)}
            </Title>
            <Text>{service.method}</Text>
            <Title
                level={5}
                className="text__color"
            >
                {localize('api-input-parameters', translations)}
            </Title>
            <Typography.Text>
                <pre style={{ backgroundColor: 'white' }}>{service.params}</pre>
            </Typography.Text>
            <Title
                level={5}
                className="text__color"
            >
                {service.titleBody}
            </Title>
            {service.titleBody ? (
                <Typography.Text>
                    <pre>{service.requestBody}</pre>
                </Typography.Text>
            ) : null}
            <Title
                level={5}
                className="text__color"
            >
                {localize('api-result-example', translations)}
            </Title>
            <Typography.Text>
                <pre>{service.resultExample}</pre>
            </Typography.Text>
        </Card>
    );

    return (
        <div
            className="api-template"
            style={{ backgroundColor: 'white', padding: 40, border: '1px grey solid' }}
        >
            <div className="api-template__header">
                <div>
                    <div
                        className="ButtonBack"
                        onClick={handleBack}
                    >
                        <ButtonBack />
                        {localize('api-back', translations)}
                    </div>
                    <h2 className="main-text">{localize('api-service-api', translations)}</h2>
                    <div>
                        {localize('api-developer-kit', translations)}
                        <ul>
                            <li className="first__list-item">
                                {localize('api-url-and-method', translations)}
                            </li>
                            <li className="first__list-item">
                                {localize('api-service-request-example', translations)}
                            </li>
                            <li className="first__list-item">
                                {localize('api-input-data-description', translations)}
                            </li>
                            <li className="first__list-item">
                                {localize('api-result-description', translations)}
                            </li>
                            <li className="first__list-item">
                                {localize('api-error-codes-description', translations)}
                            </li>
                        </ul>
                    </div>
                    <p>{localize('api-api-mechanism-steps', translations)}</p>
                    <ul className="container__list-item">
                        <li className="second__list-item">
                            {api_download_needed[0]}
                            <a href="https://www.postman.com/downloads/">
                                {localize('api-program', translations)}
                            </a>
                            &nbsp;
                            {api_download_needed[1]}
                        </li>
                        <li className="second__list-item">
                            <a
                                className="sfm-link"
                                href={pdf}
                                download="Инструкция.pdf"
                            >
                                {localize('api-download-instructions', translations)}
                            </a>
                        </li>
                        <li className="second__list-item">
                            {localize('api-fill-portal-data', translations)}
                        </li>
                    </ul>
                </div>
                <div className="table">
                    <Table
                        className="table__error-description"
                        dataSource={data(translations)}
                        columns={columns(translations)}
                        pagination={false}
                    />
                </div>
            </div>
            <div className="container__content">
                <div className="description">
                    {Array.isArray(services)
                        ? services.map((service, index) => (
                              <div key={index}>{renderService(service)}</div>
                          ))
                        : renderService(props)}
                </div>
            </div>
            <div style={{ marginTop: 20 }}>
                <Button
                    type="primary"
                    onClick={openContactsModal}
                >
                    {localize('create-request', props.translations)}
                </Button>
            </div>
            <ContactsModal
                {...props}
                title={localize('new-request', translations)}
                source={EmailSource.GlobalApi}
                translations={translations}
                isModalOpen={isOpenContactsModal}
                handleCancel={closeContactsModal}
                handleOk={handleOkContactsModal}
            />
        </div>
    );
};

export const ApiWrapper: FC = (props: any) => {
    const { children, module, pdf, translations } = props;
    const [isActive, setIsActive] = useState(false);

    const onShow = () => {
        setIsActive((prev) => !prev);
    };

    useMemo(() => {
        return React.cloneElement(children as React.ReactElement, {
            isActive,
            onShow,
            setIsActive,
        });
    }, [isActive]);

    const templateData = templates(translations, module);

    return (
        <>
            {isActive ? (
                <ApiTemplate
                    {...props}
                    {...templateData}
                    setIsActive={setIsActive}
                    translations={translations}
                    pdf={pdf}
                />
            ) : (
                React.Children.map(children, (child) => {
                    return React.cloneElement(child as React.ReactElement, {
                        isActive,
                        onShow,
                        setIsActive,
                    });
                })
            )}
        </>
    );
};

export default ApiTemplate;
